<template>
  <div
    v-if="transactionStatusPage === TransactionStatus.PENDING"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <span class="my-page-title">{{ $t(cancelReturn ? 'subscriptionCheckoutInterrupted' : 'subscriptionPaymentChecking') }}</span>
      <p class="description">{{ $t(cancelReturn ? 'subscriptionCheckoutInterruptedDescription' : 'subscriptionPaymentCheckingDescription') }}</p>
      <div
        class="transaction-status-container"
        :style="{ backgroundColor: transactionStatusData.backgroundColor }"
      >
        <spinner v-if="!cancelReturn && (transactionStatus === TransactionStatus.PENDING || isLoadingOrder)" />
        <v-icon v-else :color="transactionStatusData.color" size="40">
          {{ cancelReturn && transactionStatus === TransactionStatus.PENDING ? 'info_outline' : transactionStatusData.icon }}
        </v-icon>
        <div>
          <p
            class="transaction-status-title"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.title }}
          </p>
          <p
            class="transaction-status-description"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.description }}
          </p>
        </div>
      </div>
      <p v-if="!isLoadingOrder" class="description">{{ $t('subscriptionPaymentPendingAdvice') }}</p>
      <button class="button-no-background" @click="goToHome">{{ $t('backToEventPage') }}</button>
    </div>
    <div class="shop-section">
      <img src="/assets/tickets-visual.svg">
    </div>
  </div>
  <div
    v-else-if="transactionStatusPage === TransactionStatus.FAILED"
    class="registration-success-page-container"
  >
    <div class="registration-success-page">
      <span class="my-page-title error-title">{{ $t(paymentStatus === 'CANCELED' ? 'subscriptionPaymentCanceled' : 'subscriptionPaymentNotCompleted') }}</span>
      <p class="description">{{ $t('subscriptionPaymentNotCompletedDescription') }}</p>
      <main-button :href="repeatPurchaseUrl" color="black" style="width: 100%; margin-top: 3rem">
        {{ $t('repeatSubscriptionPurchase') }}
      </main-button>
    </div>
    <div class="shop-section">
      <img src="/assets/tickets-error-visual.svg" alt="subscription error">
    </div>
  </div>
  <div v-else class="registration-success-page-container">
    <div class="registration-success-page">
      <span class="my-page-title">{{ $t('paymentSuccessful') }}</span>
      <span class="description">
        {{ $t('subscriptionReady') }}
      </span>
      <div id="buttons-container">
        <a
          v-if="user && order"
          target="_blank"
          class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
          :href="`${customerURL}/orders/${order.uuid}`"
        >
          {{ $t('viewTheOrder') }}
        </a>
      </div>
      <button class="button-no-background" @click="goToHome">
        {{ $t('backToEventPage') }}
      </button>
    </div>
    <div class="shop-section">
      <img src="/assets/successful-payment.svg">
    </div>
  </div>
</template>

<script>
import { getOrderInfoByUUID, getPaymentStatusToken } from '@/http'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { CHECK_ORDER_WS_PAY_STATUS } from '@/graphql/order.graphql'
import { customerURL, landingURL, frontendURL } from '@/utils'
import Spinner from '@/components/shared/spinner.vue'
import i18next from 'i18next'
import { TransactionStatus } from '@/types'

export default {
  name: 'theater-subscription-successful-payment',
  components: {
    Spinner,
  },
  data() {
    return {
      user: null,
      order: null,
      organizationSlug: null,
      customerURL,
      landingURL,
      transactionStatusPage: TransactionStatus.PENDING,
      transactionStatus: TransactionStatus.PENDING,
      paymentStatus: null,
      pollCount: 0,
      pollTimer: null,
      loading: false, // Changed to false so we can show the pending UI
      isFreeOrder: false,
      orderFetchPollCount: 0,
      orderFetchPollTimer: null,
      orderFetchError: false,
      isLoadingOrder: true, // New flag to track if we're still fetching the order
    }
  },
  computed: {
    cancelReturn() {
      return this.$route.query.checkoutCanceled === '1'
    },
    TransactionStatus() {
      return TransactionStatus
    },
    repeatPurchaseUrl() {
      if (this.order && this.order.event && this.order.event.slug) {
        return `${frontendURL}/events/${this.order.event.slug}/register?lang=${i18next.language}`
      }
      if (this.organizationSlug) {
        return `${frontendURL}/organization/${this.organizationSlug}/theater-subscriptions`
      }

      return landingURL
    },
    transactionStatusData() {
      if (this.transactionStatus === TransactionStatus.PENDING) {
        return {
          title: this.$t('subscriptionPaymentUnconfirmed'),
          description: this.$t('subscriptionPaymentCheckingStatus'),
          backgroundColor: '#E6F1F8',
          color: '#0277BD',
          icon: '',
        }
      } else if (this.transactionStatus === TransactionStatus.SUCCESSFUL) {
        return {
          title: this.$t('transactionStatuses.SUCCESSFUL'),
          description: this.$t('transactionStatusesDescription.SUCCESSFUL'),
          backgroundColor: '#EEF6EF',
          color: '#0E7E1A',
          icon: 'check_circle',
        }
      } else if (this.transactionStatus === TransactionStatus.FAILED) {
        return {
          title: this.$t('transactionStatuses.FAILED'),
          description: this.$t('transactionStatusesDescription.FAILED'),
          backgroundColor: '#FBEBED',
          color: '#BD2A3C',
          icon: 'error',
        }
      }

      return {}
    },
  },
  async mounted() {
    localStorage.removeItem('order')
    await this.fetchOrderWithPolling()
  },

  beforeDestroy() {
    this.stopPolling()
    this.stopOrderFetchPolling()
  },
  methods: {
    async fetchOrderWithPolling() {
      const uuid = this.$route.params.orderUUID

      try {
        this.order = await getOrderInfoByUUID(uuid)

        if (this.order) {
          // Successfully fetched order, stop polling and process it
          this.isLoadingOrder = false
          this.stopOrderFetchPolling()
          await this.processOrder()
        } else {
          // Order is null but no error - might be invalid UUID
          // After several retries with null response, it's probably a bad UUID
          if (this.orderFetchPollCount >= 5) {
            // If consistently null, redirect to 404
            this.$router.push('/404')
          } else {
            this.scheduleNextOrderFetch()
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error)
        this.orderFetchError = true
        this.scheduleNextOrderFetch()
      }
    },

    scheduleNextOrderFetch() {
      this.orderFetchPollCount++

      let interval
      if (this.orderFetchPollCount < 10) {
        // First 10 calls: every 2 seconds
        interval = 2000
      } else if (this.orderFetchPollCount < 20) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.orderFetchPollCount < 30) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // After 30 attempts: every 30 seconds indefinitely
        interval = 30000
      }

      this.orderFetchPollTimer = setTimeout(() => {
        this.fetchOrderWithPolling()
      }, interval)
    },

    stopOrderFetchPolling() {
      if (this.orderFetchPollTimer) {
        clearTimeout(this.orderFetchPollTimer)
        this.orderFetchPollTimer = null
      }
    },

    async processOrder() {
      const lang = this.$route.params.lang || this.$route.query.lang || 'hr'

      this.isFreeOrder =
        this.order.paymentType === 'FREE' || ['EUR 0', 'EUR 0.00'].includes(this.order.price)

      if (this.isFreeOrder || this.order.status === 'SUCCESSFUL') {
        this.applyPaymentResult({ success: true, paymentStatus: 'COMPLETED' })
      }

      // Set language
      i18next.changeLanguage(lang === 'en' ? 'en' : 'hr')

      // Get user info if logged in
      await this.getUser()

      // Extract organization slug from order data if available
      if (this.order && this.order.organization) {
        this.organizationSlug = this.order.organization.slug
      }

      if (this.transactionStatusPage === TransactionStatus.PENDING) {
        this.startPolling()
      }
    },

    async getUser() {
      try {
        const { data } = await this.$apollo.query({
          query: GET_LOGGED_USER,
        })
        this.user = (data && data.me) || null
      } catch (error) {
        console.error('Error fetching user:', error)
        this.user = null
      }
    },

    goToHome() {
      // Navigate back to the organization's theater subscription page
      if (this.organizationSlug) {
        this.$router.push(`/organization/${this.organizationSlug}/theater-subscriptions`)
      } else {
        // Fallback to main site
        window.location.href = this.landingURL || 'https://www.core-event.co'
      }
    },

    async checkWsPayStatus() {
      try {
        // Prepare the order items for the query
        const items = this.order.purchases.map(purchase => ({
          orderItemId: parseInt(purchase.subscriptionId),
          count: 1,
        }))

        // Group items by orderItemId to get the count
        const groupedItems = items.reduce((acc, item) => {
          const existing = acc.find(i => i.orderItemId === item.orderItemId)
          if (existing) {
            existing.count += 1
          } else {
            acc.push({ ...item })
          }

          return acc
        }, [])

        const { data } = await this.$apollo.query({
          query: CHECK_ORDER_WS_PAY_STATUS,
          variables: {
            order: {
              uuid: this.order.uuid,
              orderId: this.order.id,
              statusToken: getPaymentStatusToken(this.order.uuid),
              eventId: this.order.eventId,
              items: groupedItems,
            },
            isTheaterSubscriptionOrder: true,
          },
          fetchPolicy: 'no-cache',
        })

        if (!this.applyPaymentResult(data && data.checkOrderWsPayStatus)) {
          this.scheduleNextPoll()
        }
      } catch (error) {
        console.error('Error checking WsPay status:', error)
        this.scheduleNextPoll()
      }
    },

    applyPaymentResult(result) {
      if (!result) return false
      this.paymentStatus = result.paymentStatus || null
      if (result.success) {
        const firstCompletion = this.transactionStatusPage !== TransactionStatus.SUCCESSFUL
        this.transactionStatus = TransactionStatus.SUCCESSFUL
        this.transactionStatusPage = TransactionStatus.SUCCESSFUL
        this.stopPolling()
        if (firstCompletion && process.env.VUE_APP_ENVIRONMENT === 'production') {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({ event: 'purchase', purchase: { currency: this.order.currency || 'EUR', value: this.order.price } })
        }

        return true
      }
      if (['FAILED', 'CANCELED', 'REFUNDED', 'PARTIALLY_REFUNDED'].includes(this.paymentStatus)) {
        this.transactionStatus = TransactionStatus.FAILED
        this.transactionStatusPage = TransactionStatus.FAILED
        this.stopPolling()

        return true
      }

      return false
    },

    scheduleNextPoll() {
      this.pollCount++

      let interval
      if (this.pollCount < 20) {
        // First 20 calls: every 3 seconds
        interval = 3000
      } else if (this.pollCount < 30) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.pollCount < 40) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // A timeout is not proof of payment failure. Keep reconciling slowly
        // until the backend confirms a terminal state or this page closes.
        interval = 30000
      }

      this.pollTimer = setTimeout(() => {
        this.checkWsPayStatus()
      }, interval)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },

    startPolling() {
      // Start polling immediately
      this.pollCount = 0
      this.checkWsPayStatus()
    },
  },
}
</script>

<style scoped>
.button-no-background {
  font-family: Raleway;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.my-page-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  text-align: left;
  font-size: 2.25rem;
  line-height: 2.8rem;
  color: #0277bd;
  margin-bottom: 1.5rem;
  margin-top: 60px;
}

.error-title {
  color: #bd2a3c !important;
}

.registration-success-page-container {
  margin: auto;
  max-width: 1440px;
  width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6rem;
}

.registration-success-page {
  align-items: flex-start;
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
}

.description {
  font-family: Source Sans Pro, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 23px;
  color: #333333;
}

#buttons-container {
  margin: 2rem 0;
  max-width: 640px !important;
  display: flex;
  flex-direction: column;
}

.main-button {
  font-size: 16px;
  height: 36px;
  font-family: Raleway, sans-serif;
  font-weight: bold;
  line-height: 1.5;
}

.shop-section {
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border-radius: 1.5rem;
}

@media screen and (max-width: 1187px) {
  .registration-success-page-container {
    padding: 0 1rem;
    justify-content: center;
    flex-direction: column;
  }

  .registration-success-page {
    padding-bottom: 4rem;
  }

  .registration-success-page-container .shop-section {
    max-width: 38rem;
    width: 100%;
    align-self: flex-end;
    margin-right: 0;
    border-radius: 1.5rem 0 0 1.5rem;
  }
}

.transaction-status-container {
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 0.75rem;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  border-radius: 62.4375rem;
  margin: 1.5rem 0;
}

.transaction-status-title {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 110%;
}

.transaction-status-description {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
}
.shop-section img { max-width: 100%; height: auto; }
@media screen and (max-width: 600px) {
  .my-page-title { margin-top: 32px; font-size: 28px; line-height: 1.2; }
  .registration-success-page { padding-bottom: 24px; }
  .transaction-status-container { border-radius: 16px; }
  .shop-section { display: none; }
  .button-no-background { min-height: 44px; text-align: left; }
}
</style>
